// Holding panel — hold-time × monthly-carry line items, with a
// financing subsection that auto-derives the mortgage line via the
// math contract's deriveMortgageMonthly helper. Manual edits to the
// mortgage line stick because they route through the same per-line
// handler as the other items.
//
// Mirrors src/components/HoldingPanel.jsx in the React standalone.
//
// The panel scaffolds its full DOM once and stores refs to every
// input + readout — render() updates values + visibility, never the
// structure — so partial decimals (e.g. typing 5.5 in "Annual
// interest rate") aren't destroyed mid-keystroke.

import {computeDealMetrics, deriveMortgageMonthly} from '../calculations';
import {IDealHoldingItem, IDealState} from '../defaults';
import {currencyInput, numInput, panelSection} from '../primitives';

export interface IHoldingPanelHost {
	getState(): IDealState;
	update(patch: Partial<IDealState>): void;
}

interface IHoldingRowRefs {
	row: HTMLElement;
	labelEl: HTMLElement;
	input: HTMLInputElement;
}

export class HoldingPanel {
	private rootEl: HTMLElement | null;
	private titleEl: HTMLElement | null = null;
	private subtitleEl: HTMLElement | null = null;
	private bodyEl: HTMLElement | null = null;

	// Top row
	private holdMonthsInput: HTMLInputElement | null = null;
	private monthlyReadoutEl: HTMLElement | null = null;

	// Financing
	private financedAmountInput: HTMLInputElement | null = null;
	private financedRateInput: HTMLInputElement | null = null;
	private financingNote: HTMLElement | null = null;

	// Holding items grid (rows persistent by item id)
	private holdingGridEl: HTMLElement | null = null;
	private holdingRows: Map<string, IHoldingRowRefs> = new Map();

	// Footer
	private totalLabelEl: HTMLElement | null = null;
	private totalValueEl: HTMLElement | null = null;

	constructor(private host: IHoldingPanelHost) {
		this.rootEl = document.getElementById('id_lb-deal-panel-holding');
		if (!this.rootEl) {
			return;
		}
		this.scaffoldShell();
		this.scaffoldBody();
		this.render();
	}

	render(): void {
		if (!this.bodyEl) {
			return;
		}
		const state = this.host.getState();
		const m = computeDealMetrics(state);
		const mode = state.mode || 'reno';

		if (this.titleEl) {
			this.titleEl.textContent = 'Holding Costs';
		}
		if (this.subtitleEl) {
			this.subtitleEl.textContent = mode === 'build'
				? 'Silent margin killer — tracks carry through build + sale'
				: 'Silent margin killer — tracks carry through reno + sale';
		}

		// Top row
		this.setInputValue(this.holdMonthsInput, state.holdMonths || 0);
		if (this.monthlyReadoutEl) {
			this.monthlyReadoutEl.textContent = `${formatCurrency(m.totalMonthlyHolding)} / mo`;
		}

		// Financing
		this.setInputValue(this.financedAmountInput, state.financedAmount || 0);
		this.setInputValue(this.financedRateInput, state.financedRatePct || 0);
		if (this.financingNote) {
			const fa = state.financedAmount || 0;
			const fr = state.financedRatePct || 0;
			if (fa > 0 && fr > 0) {
				this.financingNote.textContent =
					`Auto-fills the Mortgage line below at ${formatCurrency(deriveMortgageMonthly(fa, fr))} / mo. Edit it manually to override.`;
				this.financingNote.style.display = '';
			} else {
				this.financingNote.style.display = 'none';
			}
		}

		// Holding items
		this.renderHoldingRows(state);

		// Footer
		if (this.totalLabelEl) {
			this.totalLabelEl.textContent =
				`Total holding (${state.holdMonths || 0} mo × ${formatCurrency(m.totalMonthlyHolding)})`;
		}
		if (this.totalValueEl) {
			this.totalValueEl.textContent = formatCurrency(m.totalHoldingCosts);
		}
	}

	private renderHoldingRows(state: IDealState): void {
		if (!this.holdingGridEl) {
			return;
		}
		const presentIds = new Set<string>();
		(state.holdingItems || []).forEach(item => {
			presentIds.add(item.id);
			let row = this.holdingRows.get(item.id);
			if (!row) {
				row = this.scaffoldHoldingRow(item);
				this.holdingRows.set(item.id, row);
				this.holdingGridEl!.appendChild(row.row);
			}
			row.labelEl.textContent = item.label;
			this.setInputValue(row.input, item.monthly || 0);
		});
		for (const [id, row] of this.holdingRows.entries()) {
			if (!presentIds.has(id)) {
				row.row.remove();
				this.holdingRows.delete(id);
			}
		}
	}

	private scaffoldHoldingRow(item: IDealHoldingItem): IHoldingRowRefs {
		const row = document.createElement('div');
		row.className = 'lb-deal-line-row';
		const labelEl = document.createElement('span');
		labelEl.className = 'lb-deal-line-row__label';
		labelEl.textContent = item.label;
		row.appendChild(labelEl);
		const inputWrap = currencyInput({
			value: item.monthly || 0,
			size: 'small',
			focusKey: `hold-item-${item.id}`,
			onChange: v => {
				const stateNow = this.host.getState();
				const items: Array<IDealHoldingItem> = (stateNow.holdingItems || []).map(i =>
					i.id === item.id ? {...i, monthly: Math.max(0, v)} : i,
				);
				this.host.update({holdingItems: items});
			},
		});
		const input = inputWrap.querySelector('input') as HTMLInputElement;
		row.appendChild(inputWrap);
		return {row, labelEl, input};
	}

	private setInputValue(input: HTMLInputElement | null, value: number): void {
		if (!input || document.activeElement === input) {
			return;
		}
		input.value = value > 0 ? Number(value).toLocaleString('en-US', {maximumFractionDigits: 4}) : '';
	}

	// Editing a financing input re-derives the mortgage line item;
	// the user's manual edits to the mortgage line below stick because
	// they go through the line-item handler, not this one.
	private applyFinancing(patch: Partial<Pick<IDealState, 'financedAmount' | 'financedRatePct'>>): void {
		const state = this.host.getState();
		const newAmt = 'financedAmount' in patch ? patch.financedAmount! : (state.financedAmount || 0);
		const newRate = 'financedRatePct' in patch ? patch.financedRatePct! : (state.financedRatePct || 0);
		const items: Array<IDealHoldingItem> = (state.holdingItems || []).slice();
		const idx = items.findIndex(i => i.id === 'mortgage');
		const next: Partial<IDealState> = {...patch};
		if (idx >= 0) {
			items[idx] = {...items[idx], monthly: deriveMortgageMonthly(newAmt, newRate)};
			next.holdingItems = items;
		}
		this.host.update(next);
	}

	// ─── Scaffold ───

	private scaffoldShell(): void {
		if (!this.rootEl) {
			return;
		}
		const handles = panelSection({
			rootEl: this.rootEl,
			icon: 'calculate',
			title: 'Holding Costs',
		});
		this.titleEl = handles.titleEl;
		this.subtitleEl = handles.subtitleEl;
		this.bodyEl = handles.bodyEl;
	}

	private scaffoldBody(): void {
		if (!this.bodyEl) {
			return;
		}

		// Top row: Hold time + total monthly carry readout
		const topRow = document.createElement('div');
		topRow.className = 'lb-deal-grid lb-deal-grid--2';
		const monthsField = this.makeNumField('Hold time (months)', 'hold-months', v => {
			this.host.update({holdMonths: v});
		});
		this.holdMonthsInput = monthsField.input;
		topRow.appendChild(monthsField.wrap);
		const monthlyField = document.createElement('div');
		monthlyField.className = 'lb-deal-field';
		const monthlyLabel = document.createElement('label');
		monthlyLabel.className = 'lb-deal-field__label';
		monthlyLabel.textContent = 'Total monthly carry';
		monthlyField.appendChild(monthlyLabel);
		const monthlyVal = document.createElement('div');
		monthlyVal.className = 'lb-deal-field__readout';
		this.monthlyReadoutEl = monthlyVal;
		monthlyField.appendChild(monthlyVal);
		topRow.appendChild(monthlyField);
		this.bodyEl.appendChild(topRow);

		// Financing block
		const block = document.createElement('div');
		block.className = 'lb-deal-financing';
		const head = document.createElement('div');
		head.className = 'lb-deal-financing__head';
		const title = document.createElement('span');
		title.className = 'lb-deal-financing__title';
		title.textContent = 'Financing';
		head.appendChild(title);
		const hint = document.createElement('span');
		hint.className = 'lb-deal-financing__hint';
		hint.textContent = '— approximate monthly interest carry';
		head.appendChild(hint);
		block.appendChild(head);

		const grid = document.createElement('div');
		grid.className = 'lb-deal-grid lb-deal-grid--2';

		const amtField = this.makeCurrencyField('Financed amount', 'hold-financed-amt', v => {
			this.applyFinancing({financedAmount: v});
		});
		this.financedAmountInput = amtField.input;
		grid.appendChild(amtField.wrap);

		const rateField = this.makeNumField('Annual interest rate (%)', 'hold-financed-rate', v => {
			this.applyFinancing({financedRatePct: v});
		});
		this.financedRateInput = rateField.input;
		grid.appendChild(rateField.wrap);

		block.appendChild(grid);

		const note = document.createElement('p');
		note.className = 'lb-deal-financing__note';
		note.style.display = 'none';
		this.financingNote = note;
		block.appendChild(note);
		this.bodyEl.appendChild(block);

		// Holding items grid (rows added by renderHoldingRows)
		this.holdingGridEl = document.createElement('div');
		this.holdingGridEl.className = 'lb-deal-breakdown__grid';
		this.bodyEl.appendChild(this.holdingGridEl);

		// Footer
		const totalRow = document.createElement('div');
		totalRow.className = 'lb-deal-line-row lb-deal-line-row--summary';
		const tLabel = document.createElement('span');
		tLabel.className = 'lb-deal-line-row__label';
		this.totalLabelEl = tLabel;
		totalRow.appendChild(tLabel);
		const tVal = document.createElement('span');
		tVal.className = 'lb-deal-line-row__value';
		this.totalValueEl = tVal;
		totalRow.appendChild(tVal);
		this.bodyEl.appendChild(totalRow);
	}

	private makeNumField(label: string, focusKey: string, onChange: (v: number) => void): {wrap: HTMLElement; input: HTMLInputElement} {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const lbl = document.createElement('label');
		lbl.className = 'lb-deal-field__label';
		lbl.textContent = label;
		wrap.appendChild(lbl);
		const input = numInput({value: 0, focusKey, onChange: v => onChange(Math.max(0, v))});
		wrap.appendChild(input);
		return {wrap, input};
	}

	private makeCurrencyField(label: string, focusKey: string, onChange: (v: number) => void): {wrap: HTMLElement; input: HTMLInputElement} {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const lbl = document.createElement('label');
		lbl.className = 'lb-deal-field__label';
		lbl.textContent = label;
		wrap.appendChild(lbl);
		const fieldWrap = currencyInput({
			value: 0,
			focusKey,
			onChange: v => onChange(Math.max(0, v)),
		});
		wrap.appendChild(fieldWrap);
		const input = fieldWrap.querySelector('input') as HTMLInputElement;
		return {wrap, input};
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}
